import { ComponentType, SVGProps } from "react";
import { TriangleUpIcon, TriangleDownIcon } from "@chakra-ui/icons";
import { Box, Icon, IconProps } from "@chakra-ui/react";
import { SortDirection } from "@tanstack/react-table";
import TooltipIconButton, {
  TooltipIconButtonProps,
} from "components/Shared/TooltipIconButton";

type SortButtonProps = Omit<TooltipIconButtonProps, "icon" | "label"> & {
  IconComponent: ComponentType<SVGProps<SVGElement>> | ComponentType<IconProps>;
  isSorted?: boolean;
  label: string;
};

function SortButton({
  IconComponent,
  isSorted,
  label,
  ...rest
}: SortButtonProps) {
  return (
    <TooltipIconButton
      icon={<Icon as={IconComponent} boxSize=".75em" />}
      variant="ghost"
      size="xs"
      pb=".5em"
      pt=".5em"
      h="auto"
      opacity={0.4}
      tooltipProps={{ openDelay: 750, closeOnMouseDown: true }}
      _active={{ opacity: 1, color: "yellowGreen" }}
      _hover={{ opacity: 1, backgroundColor: "rgba(255, 255, 255, .25)" }}
      data-active={isSorted || undefined}
      label={
        isSorted ? (
          <>
            {label}{" "}
            <Box as="b" fontSize=".75em">
              (Active)
            </Box>
          </>
        ) : (
          label
        )
      }
      {...rest}
    />
  );
}

type Props = {
  isSorted?: false | SortDirection;
  onSortAscending: () => void;
  onSortDescending: () => void;
};

export default function SortControls({
  isSorted = false,
  onSortAscending,
  onSortDescending,
}: Props) {
  return (
    <Box
      className="sort-controls"
      display="inline-flex"
      flexFlow="column"
      mt="-.5em"
      mb="-.5em"
      ml=".4em"
      verticalAlign="middle"
    >
      <SortButton
        IconComponent={TriangleUpIcon}
        label="Sort Ascending"
        pb={0}
        borderBottomRadius={0}
        onClick={onSortAscending}
        isSorted={isSorted === "asc"}
      />
      <SortButton
        IconComponent={TriangleDownIcon}
        label="Sort Descending"
        pt={0}
        borderTopRadius={0}
        onClick={onSortDescending}
        isSorted={isSorted === "desc"}
      />
    </Box>
  );
}
